import { collect } from 'collect.js';
import { DeliveryMode, DeliveryProvider } from '../exports/Constants';

/**
 * Pure service-provider regional-config predicates. See
 * tests/services/tenant/serviceProviderConfig.test.js.
 */

/**
 * The delivery providers (incl. standalone) a service provider's restaurants can use: the
 * region's list narrowed by Shop to the couriers the partner has an account for. Falls back
 * to the region's list for Shop builds that do not send `available_delivery_providers` yet.
 */
export function availableDeliveryProviders(serviceProvider) {
  return (
    serviceProvider?.available_delivery_providers ??
    serviceProvider?.regional_config?.supported_delivery_providers ??
    []
  );
}

/**
 * The delivery providers (incl. standalone) one restaurant can use: its own region's list
 * narrowed by Shop to the couriers its partner has an account for, which is what Shop
 * validates its courier settings against. Falls back to the partner's list for Shop builds
 * that do not send the company's `available_delivery_providers` yet.
 */
export function companyAvailableDeliveryProviders(company, serviceProvider) {
  return company?.available_delivery_providers ?? availableDeliveryProviders(serviceProvider);
}

/**
 * Whether the service provider supports any external (non-standalone) delivery
 * provider — i.e. delivery handled by a third party rather than in-house.
 */
export function hasExternalDeliveryProviders(supportedProviders) {
  return collect(supportedProviders)
    .filter((item) => item !== DeliveryProvider.Standalone)
    .isNotEmpty();
}

/**
 * Whether the Delivery mode card is shown: the partner offers a courier, or the restaurant
 * still has external delivery saved from before its partner lost that courier, so its
 * manager can switch back to restaurant drivers.
 */
export function canChooseDeliveryMode(availableProviders, company) {
  return (
    availableProviders.length > 1 ||
    company?.delivery_mode === DeliveryMode.External ||
    hasExternalDeliveryProviders(company?.delivery_providers)
  );
}

/**
 * The region's providers the restaurant can be shown: those its partner can use, plus any it
 * already has saved, so a provider the partner has since lost does not silently vanish.
 */
export function offeredDeliveryProviders(regionProviders, availableProviders, savedProviders) {
  return collect(regionProviders ?? [])
    .filter((item) => availableProviders.includes(item) || (savedProviders ?? []).includes(item))
    .toArray();
}
